import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { useContext } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Settings, LogOut, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { EntriesTab } from '../components/admin/EntriesTab';
import { SummaryTab } from '../components/admin/SummaryTab';
import { PricesTab } from '../components/admin/PricesTab';
import { PaymentsTab } from '../components/admin/PaymentsTab';

const AdminDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [parts, setParts] = useState([]);
  const [showNotifDialog, setShowNotifDialog] = useState(false);
  const [shiftTime, setShiftTime] = useState('16:30');
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  useEffect(() => {
    loadMasterData();
    // Load current shift time
    axios.get(`${API}/push/shift-time`).then(r => setShiftTime(r.data.shift_end_time)).catch(() => {});
  }, []);

  const saveShiftTime = async () => {
    try {
      await axios.post(`${API}/push/shift-time`, { shift_end_time: shiftTime });
      toast.success(`Часът е запазен: ${shiftTime}`);
      setShowNotifDialog(false);
    } catch { toast.error('Грешка при запазване'); }
  };

  const loadMasterData = async () => {
    try {
      const [empRes, partsRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/parts`)
      ]);
      setEmployees(empRes.data);
      setParts(partsRes.data);
    } catch {
      toast.error('Грешка при зареждане на данни');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-5 flex justify-between items-center gap-2">
          <h1 className="heading-font text-base sm:text-2xl md:text-3xl font-bold text-slate-900 uppercase leading-tight truncate">
            <span className="hidden sm:inline">Административен панел</span>
            <span className="sm:hidden">Адм. панел</span>
          </h1>
          <div className="flex gap-1 sm:gap-2 shrink-0">
            <Button
              data-testid="notif-settings-button"
              onClick={() => setShowNotifDialog(true)}
              variant="outline"
              size="sm"
              className="border-2 hover:border-red-600 px-2 sm:px-3"
              title="Час на известие"
            >
              <Bell className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs">Известия</span>
            </Button>
            <Button
              data-testid="management-button"
              onClick={() => navigate('/admin/management')}
              variant="outline"
              size="sm"
              className="border-2 hover:border-red-600 px-2 sm:px-3"
            >
              <Settings className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs">Управление</span>
            </Button>
            <Button
              data-testid="logout-button"
              onClick={logout}
              variant="outline"
              size="sm"
              className="border-2 hover:border-red-600 hover:text-red-600 px-2 sm:px-3"
            >
              <LogOut className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs">Изход</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-8">
        <Tabs defaultValue="entries" className="w-full">
          <TabsList className="grid grid-cols-4 w-full mb-4 sm:mb-6 h-auto">
            <TabsTrigger value="entries" className="uppercase text-xs px-1 py-2 sm:px-3 leading-tight">Записи</TabsTrigger>
            <TabsTrigger value="summary" className="uppercase text-xs px-1 py-2 sm:px-3 leading-tight">Обобщение</TabsTrigger>
            <TabsTrigger value="rates" className="uppercase text-xs px-1 py-2 sm:px-3 leading-tight">Цени</TabsTrigger>
            <TabsTrigger value="payments" className="uppercase text-xs px-1 py-2 sm:px-3 leading-tight">Доплащания</TabsTrigger>
          </TabsList>

          <TabsContent value="entries">
            <EntriesTab employees={employees} parts={parts} />
          </TabsContent>

          <TabsContent value="summary">
            <SummaryTab />
          </TabsContent>

          <TabsContent value="rates">
            <PricesTab employees={employees} />
          </TabsContent>

          <TabsContent value="payments">
            <PaymentsTab employees={employees} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Notification Settings Dialog */}
      <Dialog open={showNotifDialog} onOpenChange={setShowNotifDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="heading-font text-xl uppercase flex items-center gap-2">
              <Bell className="w-5 h-5" /> Час на известие
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-600">
              Работниците ще получат push известие ако имат незапратени записи в посоченото време.
            </p>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Край на смяната</Label>
              <Input
                type="time"
                value={shiftTime}
                onChange={(e) => setShiftTime(e.target.value)}
                className="border-2 text-lg font-mono"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={saveShiftTime} className="flex-1 bg-red-600 hover:bg-red-700 font-bold uppercase">
                Запази
              </Button>
              <Button onClick={() => setShowNotifDialog(false)} variant="outline" className="flex-1 border-2">
                Отказ
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
